import { FC, useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'src/components/authentication/i18n';
import { useAuth } from 'src/contexts/JWTContext';
import { useSettings } from 'src/contexts/SettingsContext';
import useGeneralError from 'src/hooks/useGeneralError';
import {
  ButtonProps,
} from 'material-hu';
import oktaLogo from 'src/assets/okta.svg';
import { SelectInstance } from 'src/components/authentication';
import LoginButton from 'src/pages/authentication/components/LoginButton';
import { LoginInstance } from 'src/types/login';
import { getOktaInstances } from 'src/services/authService';
import 'src/components/authentication/i18n';

export type OktaLoginProps = ButtonProps & {
  onLogin: () => void;
  onLoginFinally: () => void;
};

export const OktaLogin: FC<OktaLoginProps> = (props) => {
  const {
    onLogin = () => null,
    onLoginFinally = () => null,
    ...buttonProps
  } = props;

  const [instances, setInstances] = useState<LoginInstance[]>([]);
  const [accessToken, setAccessToken] = useState<string>(null);

  const { oktaAuth } = useOktaAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const showGeneralError = useGeneralError();
  const { oktaLogin } = useAuth();
  const { setPrimary, resetPrimary } = useSettings();

  useEffect(() => {
    resetPrimary();
  }, [resetPrimary]);

  useEffect(() => {
    if (window.location.hash === '#instancesOkta' && !hasInstances) {
      navigate('');
    }
  }, [window.location.hash]);

  const handleSelectInstance = async (instance: LoginInstance) => {
    try {
      navigate('');
      await login(accessToken, instance);
    }
    catch (err) {
      showGeneralError(err, t('ERROR_OKTA_LOGIN'));
    }
  };

  const login = async (accessToken: string, instance: LoginInstance) => {
    setPrimary(instance.color);
    const body = {
      accessToken,
      instanceId: instance.id,
    };
    await oktaLogin(body);
  };

  const handleCancelInstance = () => {
    navigate('');
    setInstances([]);
  };

  const handleLogin = async () => {
    try {
      onLogin();
      const oktaData = await oktaAuth.token.getWithPopup({
        prompt: 'login',
      });
      const { tokens: { accessToken: { accessToken } } } = oktaData;

      const response = await getOktaInstances(accessToken);
      if (response.data?.length) {
        setInstances(response.data);
        setAccessToken(accessToken);

        if (response.data.length === 1) {
          await login(accessToken, response.data[0]);
        }
        else {
          navigate('#instancesOkta');
        }
      }
      else {
        enqueueSnackbar(
          t('ERROR_OKTA_GET_INSTANCES'),
          { variant: 'error' },
        );
      }
    }
    catch (err) {
      showGeneralError(err, t('ERROR_OKTA_LOGIN'));
    }
    finally {
      onLoginFinally();
    }
  };

  const hasInstances = instances.length > 0;

  return (
    <>
      <LoginButton
        logo={oktaLogo}
        label="Okta"
        onClick={handleLogin}
        {...buttonProps}
      />
      <SelectInstance
        open={hasInstances && window.location.hash === '#instancesOkta'}
        onSelect={handleSelectInstance}
        onCancel={handleCancelInstance}
        instances={instances}
      />
    </>
  );
};

export default OktaLogin;
